import { Button } from '@mui/material'
import PropTypes, { InferProps } from 'prop-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'
import ButtonProcessing from 'src/components/ButtonProcessing'
import Modal from 'src/components/Modal'
import WarningModal from 'src/components/WarningModal'
import { ReactSelectStyles } from 'src/components/reactSelectStyles'
import {
  getAllDoctorsFromBasPractice,
  getBasPracticeByCompanyLocationId
} from 'src/pages/Admins/Companies/helper/externalFunctions'
import {
  // massCreateExaminatiosRecord,
  massUpdateExaminatiosRecord
} from 'src/redux/actions/examinationActions'
import { useAppDispatch, useAppSelector } from 'src/redux/hook'

export function Actions({ examinations, companyLocationId }: InferProps<typeof Actions.propTypes>) {
  const [isSaveModal, setIsSaveModal] = useState(false)
  const [isCancelModal, setIsCancelModal] = useState(false)
  const [isWarningModal, setIsWarningModal] = useState(false)
  const [displaySuccessButton, setDisplaySuccessButton] = useState(false)
  const [doctorsOptions, setDoctorsOptions] = useState([])
  const [doctorOption, setDoctorsOption] = useState<{ value: string; label: string } | null>(null)

  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const { examinationRecordMassMark, userSignin } = useAppSelector((state) => state)
  const { loading: massMarkLoading, success: massMarkSuccess } = examinationRecordMassMark
  const { user: loggedUser } = userSignin

  const { appReqId } = location.state

  useEffect(() => {
    if (loggedUser) {
      setDoctorsOption({
        label: `${loggedUser.firstName} ${loggedUser.lastName}`,
        value: loggedUser.id
      })
    }
  }, [loggedUser])

  useEffect(() => {
    ;(async () => {
      const doctorsHelper: any = []
      if (companyLocationId) {
        const response = await getBasPracticeByCompanyLocationId({ id: companyLocationId })
        if (response) {
          const doctors = await getAllDoctorsFromBasPractice({ id: response[0].id })
          if (doctors) {
            doctors.forEach((doctor: any) => {
              doctorsHelper.push({
                label: `${doctor.firstName} ${doctor.lastName}`,
                value: doctor.id
              })
            })
            setDoctorsOptions(doctorsHelper)
          }
        }
      }
    })()
  }, [companyLocationId])

  useEffect(() => {
    if (displaySuccessButton) {
      setTimeout(() => {
        setDisplaySuccessButton(false)
      }, 1000)
    }
  }, [displaySuccessButton])

  useEffect(() => {
    if (massMarkSuccess) {
      setTimeout(() => {
        window.history.back()
        dispatch({ type: 'EXAMINATION_RECORD_MASS_MARK_RESET' })
      }, 2000)
      setDisplaySuccessButton(true)
    }
  }, [massMarkSuccess])

  const checkExaminationLength = (examinations: any) => {
    const isAllExaminationHaveName = examinations?.every(
      (examination: {
        examinationName: string
        performedDate: string
        nextExaminationDate: string
        examinationCheckup: string
      }) => {
        const { examinationName, performedDate, nextExaminationDate, examinationCheckup } =
          examination
        if (examinationName && performedDate && nextExaminationDate && examinationCheckup) {
          return true
        }
        return false
      }
    )
    if (!isAllExaminationHaveName) {
      setIsWarningModal(true)
      return
    }
    setIsSaveModal(true)
  }

  const onSubmit = () => {
    const filteredExaminations: any = []
    examinations?.forEach((examination) => {
      const {
        id,
        status,
        examinationNote,
        examinationCheckup,
        performedDate,
        nextExaminationDate,
        examinationRequired,
        employeeId,
        companyId,
        companyLocationId,
        examinationTypeId
      } = examination

      filteredExaminations.push({
        id,
        status,
        examinationNote,
        examinationCheckup,
        performedDate,
        nextExaminationDate,
        examinationRequired,
        employeeId,
        companyId,
        companyLocationId,
        examinationTypeId
      })
    })
    const data = {
      appointmentRequestId: appReqId,
      examinations: filteredExaminations,
      doctorId: doctorOption?.value
    }

    dispatch(massUpdateExaminatiosRecord(data))
    setIsSaveModal(false)
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
  }

  return (
    <div className='mt-8'>
      {isSaveModal && (
        <Modal
          onCancel={() => setIsSaveModal(false)}
          onConfirm={() => onSubmit()}
          message={t('MODAL.SAVE_RECORD')}
        />
      )}
      {isWarningModal && (
        <WarningModal
          onCancel={() => setIsWarningModal(false)}
          message={t('WARNING_MODAL.WARNING_EXAMINATIONS')}
        />
      )}
      {isCancelModal && (
        <Modal
          onCancel={() => setIsCancelModal(false)}
          onConfirm={() =>
            navigate('/scheduled-appointment-overview', {
              replace: true
            })
          }
          message={t('MODAL.CANCEL')}
        />
      )}
      <div className='flex flex-col items-center justify-between md:flex-row-reverse gap-2'>
        <div className='flex gap-2 w-full'>
          <div className='custom-select-input w-full md:max-w-[200px]'>
            <Select
              value={doctorOption}
              onChange={(val) => setDoctorsOption(val)}
              options={doctorsOptions}
              styles={ReactSelectStyles}
            />
          </div>
          <Button
            onClick={() => checkExaminationLength(examinations)}
            className={`main-btn border ${
              (massMarkLoading || displaySuccessButton) && 'processing'
            }`}
            disabled={massMarkLoading || displaySuccessButton}
            variant='contained'
          >
            {massMarkLoading || displaySuccessButton ? (
              <ButtonProcessing displaySuccessButton={displaySuccessButton} />
            ) : (
              t('BUTTONS.SAVE')
            )}
          </Button>
          <Button
            onClick={() => setIsCancelModal(true)}
            className='secondary-btn border'
            variant='contained'
          >
            {t('BUTTONS.CANCEL')}
          </Button>
        </div>
      </div>
    </div>
  )
}

Actions.propTypes = {
  examinations: PropTypes.array,
  certificate: PropTypes.any,
  companyLocationId: PropTypes.string
}
